/**
 * Feed Play to Full3d Conversion Analytics
 *
 * Handles dataLayer event emission, account ID hashing, and single-use
 * session-scoped attribution for measuring Feed CTA to full3d page conversions.
 */

import { z } from 'zod'

/**
 * Typed dataLayer event dispatcher
 */
function ensureDataLayer(): (FeedAnalyticsEvent | Record<string, unknown>)[] {
  if (typeof window === 'undefined') return []
  window.dataLayer = window.dataLayer || []
  return window.dataLayer
}

/**
 * Push a Feed analytics event to the dataLayer.
 * Safe to call even if dataLayer is unavailable.
 */
export function pushFeedAnalyticsEvent(event: FeedAnalyticsEvent): void {
  try {
    if (typeof window === 'undefined') return
    const dataLayer = ensureDataLayer()
    dataLayer.push(event)
  } catch (error) {
    // Silently fail; analytics issues must not block navigation or rendering
    // eslint-disable-next-line no-console
    console.debug('[Feed Analytics] Failed to push event:', error)
  }
}

/**
 * Hash an HTC account ID using SHA-256 Web Crypto API.
 * Returns the lowercase hex digest, or null if hashing fails or no ID provided.
 */
export async function hashAccountId(accountId: string | null | undefined): Promise<string | null> {
  if (!accountId) return null

  try {
    const encoder = new TextEncoder()
    const data = encoder.encode(accountId)
    const hashBuffer = await crypto.subtle.digest('SHA-256', data)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('')
  } catch (error) {
    // eslint-disable-next-line no-console
    console.debug('[Feed Analytics] Failed to hash account ID:', error)
    return null
  }
}

/**
 * Feed navigation attribution record, stored in sessionStorage
 */
export interface FeedAttributionRecord {
  id: string // Unique identifier appended to URL
  hubSid: string // Expected destination room ID
  createdAt: number // Timestamp in milliseconds
}

const ATTRIBUTION_STORAGE_KEY = '__feed_attribution'
const ATTRIBUTION_EXPIRY_MS = 5 * 60 * 1000 // 5 minutes

const feedAttributionRecordSchema = z.object({
  id: z.string().min(1),
  hubSid: z.string().min(1),
  createdAt: z.number().finite(),
})

function removeFeedAttribution(): boolean {
  window.sessionStorage.removeItem(ATTRIBUTION_STORAGE_KEY)
  return window.sessionStorage.getItem(ATTRIBUTION_STORAGE_KEY) === null
}

/**
 * Create a new single-use Feed navigation attribution record.
 * Stores it in sessionStorage bound to a specific destination hubSid.
 * Returns the unique identifier to attach to the destination URL.
 */
export function createFeedAttribution(hubSid: string): string | null {
  try {
    if (typeof window === 'undefined' || typeof window.sessionStorage === 'undefined') {
      return null
    }

    const id = crypto.randomUUID()

    const record: FeedAttributionRecord = {
      id,
      hubSid,
      createdAt: Date.now(),
    }

    window.sessionStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(record))
    return id
  } catch (error) {
    // eslint-disable-next-line no-console
    console.debug('[Feed Analytics] Failed to create attribution:', error)
    return null
  }
}

/**
 * Validate and retrieve the current Feed attribution record from sessionStorage.
 * Returns null if missing, expired, or parsing fails.
 */
export function getFeedAttribution(): FeedAttributionRecord | null {
  try {
    if (typeof window === 'undefined' || typeof window.sessionStorage === 'undefined') {
      return null
    }

    const stored = window.sessionStorage.getItem(ATTRIBUTION_STORAGE_KEY)
    if (!stored) return null

    const parsedRecord: unknown = JSON.parse(stored)
    const result = feedAttributionRecordSchema.safeParse(parsedRecord)
    if (!result.success) {
      removeFeedAttribution()
      return null
    }

    const record: FeedAttributionRecord = result.data

    const age = Date.now() - record.createdAt
    if (age < 0 || age > ATTRIBUTION_EXPIRY_MS) {
      removeFeedAttribution()
      return null
    }

    return record
  } catch (error) {
    try {
      removeFeedAttribution()
    } catch {
      // Storage is unavailable; preserve the fail-closed result.
    }
    // eslint-disable-next-line no-console
    console.debug('[Feed Analytics] Failed to retrieve attribution:', error)
    return null
  }
}

/**
 * Validate and consume an attribution bound to its URL identifier and destination.
 * Returns the consumed record, or null when it is invalid or unavailable.
 */
export function consumeFeedAttribution(
  urlId: string,
  destinationHubSid: string,
): FeedAttributionRecord | null {
  try {
    const record = getFeedAttribution()
    if (!record || record.id !== urlId || record.hubSid !== destinationHubSid) return null

    if (!removeFeedAttribution()) return null

    return record
  } catch (error) {
    // eslint-disable-next-line no-console
    console.debug('[Feed Analytics] Failed to consume attribution:', error)
    return null
  }
}
